import { CdkVirtualScrollViewport, CdkFixedSizeVirtualScroll, CdkVirtualForOf } from '@angular/cdk/scrolling';
import { AfterViewInit, ChangeDetectionStrategy, Component, ContentChildren, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnInit, Output, QueryList, SimpleChanges, ViewChild, inject } from '@angular/core';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import * as _ from 'lodash';
import { CompactListColumn, CompactListFilter, CompactListItem } from '../../models';
import { TranslateModule } from '@ngx-translate/core';
import { WarningIconComponent } from '../warning-icon/warning-icon.component';
import { SpeedDotsComponent } from '../speed-dots/speed-dots.component';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgStyle, NgClass, NgTemplateOutlet } from '@angular/common';
import { CustomCompactListColumnDirective } from './directives/custom-compact-list-column/custom-compact-list-column.directive';

@Component({
    selector: 'app-compact-list',
    templateUrl: './compact-list.component.html',
    styleUrls: ['./compact-list.component.scss'],
    imports: [NgStyle, MatTooltipModule, MatMenuModule, MatCheckboxModule, NgClass, CdkVirtualScrollViewport, CdkFixedSizeVirtualScroll, CdkVirtualForOf, SpeedDotsComponent, WarningIconComponent, TranslateModule, NgTemplateOutlet],
    changeDetection: ChangeDetectionStrategy.Eager
})
export class CompactListComponent implements OnInit, OnChanges, AfterViewInit {
    private readonly element = inject(ElementRef);
    private readonly zone = inject(NgZone);

    @ViewChild(CdkVirtualScrollViewport) viewPort: CdkVirtualScrollViewport;
    @ContentChildren(CustomCompactListColumnDirective) customColumnList?: QueryList<CustomCompactListColumnDirective>;

    @Input() columns: CompactListColumn[] = [];
    @Input() filter: CompactListFilter;
    @Input() items: CompactListItem[];
    @Input() isSortable = true;
    @Input() canToggleColumns = true;
    @Input() dynamicHeight = false;
    @Input() hideHeader = false;

    @Output() itemSelected = new EventEmitter<any>();
    @Output() columnsUpdated = new EventEmitter<CompactListColumn[]>();
    @Output() customColumnButtonClick = new EventEmitter();

    public tableColumns: string[] = [];
    public sortedItems: any[];
    public calculatedColumnWidth = {}; // key and then the width of each column

    private _resizeObserver: ResizeObserver;
    private _componentWidth: number;


    constructor() {
        this._resizeObserver = new ResizeObserver(entries => this.zone.run(() => this._observeSizeChange(entries)));
    }

    ngOnChanges(simpleChanges: SimpleChanges) {
        if (simpleChanges.columns) {
            this._recalculateColumnWidth();
            this._updateSortedItems();
        } else if (simpleChanges.items) {
            this._updateSortedItems();
        }
    }

    ngOnInit() {
        this._resizeObserver.observe(this.element.nativeElement);
    }

    ngAfterViewInit() {
        // Timeout needed to put action last in scope thread que
        setTimeout(() => {
            this._scrollToHighlighted();
        }, 0);
    }

    public getDynamicHeight() {
        if (this.dynamicHeight) {
            return this.sortedItems.length * 35 + 'px';
        }

        return null;
    }

    public itemClicked(item: CompactListItem) {
        this.itemSelected.next(item);
    }

    public toggleColumn(column: CompactListColumn) {
        if (!this.isSortable) {
            return;
        }
        this.columns = _.map(this.columns, c => {
            if (c.key === column.key) {
                c.sort = c.sort === undefined || c.sort === 'desc' ? 'asc' : 'desc';
                return c;
            } else {
                c.sort = undefined;
                return c;
            }
        });

        this._updateSortedItems();
        this.columnsUpdated.next(this.columns);
    }

    public changeColumnVisibility(column: CompactListColumn, event: MatCheckboxChange) {
        this.columns = _.map(this.columns, c => {
            if (c.key === column.key) {
                c.visible = event.checked;
            }
            return c;
        });

        this._recalculateColumnWidth();
        this.columnsUpdated.next(this.columns);
    }

    public trackByFunction(index: number, item: any): string | number {
        if (item?.id) return item.id;
        if (item?.key) return item.key;
        if (item?.inventoryNumber) return item.inventoryNumber;
        if (item?.blockNumber) return `${item.blockNumber}-${index}`;
        
        return index;
    }

    private _scrollToHighlighted() {
        this.viewPort?.scrollToIndex(_.indexOf(this.items, _.find(this.items, it => it.highlighted || it.semiHighlighted)), 'smooth');
    }

    private _updateSortedItems() {
        if (this.columns) {
            this.tableColumns = _.map(this.columns, 'key');
            const sortByColumn = _.find(this.columns, (column: CompactListColumn) => column.sort !== undefined);
            if (sortByColumn) {
                // set sorted list
                this.sortedItems = _.chain(this.items)
                    .orderBy((item) => {
                        if (sortByColumn.sortNumeric === true) {
                            if (isNaN(parseInt(item.properties[sortByColumn.sortByKey ?? sortByColumn.key], 10))) {
                                return sortByColumn.sort === 'asc' ? Infinity : -Infinity;
                            }
                            return parseInt(item.properties[sortByColumn.sortByKey ?? sortByColumn.key], 10);
                        } else {
                            return item.properties[sortByColumn.sortByKey ?? sortByColumn.key];
                        }
                    }, sortByColumn.sort as any)
                    .value();
            } else {
                this.sortedItems = this.items;
            }
        }
    }

    private _observeSizeChange(entries: ResizeObserverEntry[]): void {
        this._componentWidth = entries[0]?.target?.clientWidth;
        this._recalculateColumnWidth();
    }

    private _recalculateColumnWidth() {
        // Visible columns
        const visibleColumns = _.filter(this.columns, column => column.visible);
        const totalColumnWidth = _.sumBy(visibleColumns, column => column.width);

        // Width left from the columns to distribute, we have also som padding here so adjust a little bit
        const columnPadding = 10;
        const menuPadding = 15;
        const availableWidth = (this._componentWidth) - (totalColumnWidth + visibleColumns.length * columnPadding) - menuPadding;

        const newColumnWidth = {};
        _.each(visibleColumns, column => {
            // To make the columns have the same ratio, add certain percentage to each column
            const percentage = (column.width / totalColumnWidth);
            const widthToAdd = Math.floor(percentage * availableWidth);

            newColumnWidth[column.key] = column.width + widthToAdd;
        });

        this.calculatedColumnWidth = newColumnWidth;
    }
}
